import { Checkbox, Flex, Image, Input, InputGroup, InputRightElement, Text, VStack } from '@chakra-ui/react';
import { useId, type Ref } from 'react';
import { background, text } from '@/theme/colors/palettes';
import iconsPng from '@/assets/img/png/icons';

type CreatorNameSectionProps = {
  realName: string;
  creatorName: string;
  keepNameSameAsRealName: boolean;
  onCreatorNameChange: (value: string) => void;
  onKeepNameSameAsRealNameChange: (checked: boolean) => void;
  error?: string;
  isDisabled?: boolean;
  headingColor?: string;
  textColor?: string;
  inputRef?: Ref<HTMLInputElement>;
  onBlur?: () => void;
};

const CreatorNameSection = ({
  realName,
  creatorName,
  keepNameSameAsRealName,
  onCreatorNameChange,
  onKeepNameSameAsRealNameChange,
  error,
  isDisabled = false,
  headingColor = text.darkBlue,
  textColor = text.veryLightBlue,
  inputRef,
  onBlur,
}: CreatorNameSectionProps) => {
  const id = useId();
  return (
    <VStack width={'100%'} spacing={'6px'} alignItems={'stretch'}>
      <Text
        id={`${id}-label`}
        color={headingColor}
        fontSize={'18px'}
        lineHeight={'25px'}
        fontWeight={700}
        textAlign={'center'}
      >
        Creator Name
      </Text>
      <Text
        id={`${id}-description`}
        fontFamily={'Urbanist'}
        fontWeight={600}
        color={textColor}
        textAlign={'center'}
        fontSize={'14px'}
        lineHeight={'20px'}
        opacity={0.7}
        paddingX={'70px'}
      >
        This will appear on your public creator profile. Your real first and last name can be used for legal purposes.
      </Text>
      <InputGroup width={'300px'} maxWidth={'100%'} alignSelf={'center'} marginTop={'10px'} marginBottom={Boolean(error) ? 0 : '10px'}>
        <Input
          ref={inputRef}
          name={'creatorName'}
          value={keepNameSameAsRealName ? realName : creatorName}
          onChange={(event) => onCreatorNameChange(event.target.value)}
          onBlur={onBlur}
          isReadOnly={keepNameSameAsRealName}
          isDisabled={isDisabled}
          aria-labelledby={`${id}-label`}
          aria-describedby={`${id}-description${error ? ` ${id}-error` : ''}`}
          isInvalid={Boolean(error)}
          height={'54px'}
          background={'white'}
          border={'1px solid'}
          borderColor={background.darkGray}
          borderRadius={'10px'}
          paddingX={'22px'}
          paddingRight={'60px'}
          fontSize={'14px'}
          fontWeight={600}
          boxShadow={'0px 2px 4px 0px #0041790D'}
          placeholder={'Creator Name'}
          color={keepNameSameAsRealName ? text.mediumGray : text.mediumBlue}
          cursor={keepNameSameAsRealName ? 'default' : undefined}
        />
        {keepNameSameAsRealName && (
          <InputRightElement height={'54px'} width={'54px'} pointerEvents={'none'}>
            <Flex
              width={'22px'}
              height={'22px'}
              borderRadius={'full'}
              background={text.mediumGray}
              color={'white'}
              alignItems={'center'}
              justifyContent={'center'}
            >
              <Image alt="lock" src={iconsPng.lock} width={18} height={18} />
            </Flex>
          </InputRightElement>
        )}
      </InputGroup>
      {error && (
        <Text id={`${id}-error`} role={'alert'} color={'extra.orange'} fontSize={'14px'} textAlign={'center'}>
          {error}
        </Text>
      )}

      <VStack alignItems={'stretch'} paddingX={'22px'} spacing={'10px'}>
        <Checkbox
          isChecked={keepNameSameAsRealName}
          isDisabled={isDisabled}
          onChange={(event) => onKeepNameSameAsRealNameChange(event.target.checked)}
          padding={'12px 18px'}
          alignSelf={'center'}
          backgroundColor={'rgba(255, 255, 255, 0.2)'}
          boxShadow={'0px 2px 4px 0px #0041790D'}
          size={'lg'}
          colorScheme={'primary'}
          spacing={'12px'}
        >
          <Text fontFamily={'Urbanist'} fontSize={'16px'} fontWeight={500} color={textColor}>
            Keep same as my real name
          </Text>
        </Checkbox>
      </VStack>
    </VStack>
  );
};

export default CreatorNameSection;
